import { useEffect, useState } from 'react';
import { Input } from './input';

const MAX_AGE_YEARS = 120;

function digitsOnly(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength);
}

function daysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

// null — дата коректна (або поле порожнє, воно необов'язкове).
function validate(day: string, month: string, year: string): string | null {
  if (!day && !month && !year) return null;
  if (day.length < 2 || month.length < 2 || year.length < 4) {
    return 'Вкажіть повну дату (ДД.ММ.РРРР)';
  }
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (m < 1 || m > 12) return 'Місяць має бути від 01 до 12';
  if (d < 1 || d > daysInMonth(m, y)) return 'Такого дня в цьому місяці не існує';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(y, m - 1, d);
  if (date.getTime() > today.getTime()) return 'Дата народження не може бути в майбутньому';

  const minYear = today.getFullYear() - MAX_AGE_YEARS;
  if (y < minYear) return `Рік народження не може бути раніше ${minYear}`;

  return null;
}

// Три власних поля ДД/ММ/РРРР замість нативного <input type="date"> —
// браузерний datepicker (навіть у темному color-scheme) не можна
// підлаштувати під гострі кути й акцентний колір сайту.
export function DateInput({
  value,
  onChange,
  onValidityChange,
}: {
  value: string; // '' або 'YYYY-MM-DD'
  onChange: (value: string) => void;
  onValidityChange?: (isValid: boolean) => void;
}) {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!value) {
      setDay('');
      setMonth('');
      setYear('');
      return;
    }
    const [y, m, d] = value.split('-');
    setYear(y ?? '');
    setMonth(m ?? '');
    setDay(d ?? '');
  }, [value]);

  const commit = (nextDay: string, nextMonth: string, nextYear: string) => {
    const error = validate(nextDay, nextMonth, nextYear);
    onValidityChange?.(error === null);
    if (error === null && nextDay.length === 2 && nextMonth.length === 2) {
      onChange(`${nextYear}-${nextMonth}-${nextDay}`);
    } else {
      onChange('');
    }
  };

  const error = validate(day, month, year);

  return (
    <div>
      <div className="flex gap-2">
        <Input
          placeholder="ДД"
          inputMode="numeric"
          aria-label="День народження"
          value={day}
          onChange={(e) => {
            const next = digitsOnly(e.target.value, 2);
            setDay(next);
            commit(next, month, year);
          }}
          onBlur={() => setTouched(true)}
          className={`w-[72px] text-center ${touched && error ? 'border-warn' : ''}`}
        />
        <Input
          placeholder="ММ"
          inputMode="numeric"
          aria-label="Місяць народження"
          value={month}
          onChange={(e) => {
            const next = digitsOnly(e.target.value, 2);
            setMonth(next);
            commit(day, next, year);
          }}
          onBlur={() => setTouched(true)}
          className={`w-[72px] text-center ${touched && error ? 'border-warn' : ''}`}
        />
        <Input
          placeholder="РРРР"
          inputMode="numeric"
          aria-label="Рік народження"
          value={year}
          onChange={(e) => {
            const next = digitsOnly(e.target.value, 4);
            setYear(next);
            commit(day, month, next);
          }}
          onBlur={() => setTouched(true)}
          className={`w-[92px] text-center ${touched && error ? 'border-warn' : ''}`}
        />
      </div>
      {touched && error && <p className="mt-1 text-[12px] text-warn">{error}</p>}
    </div>
  );
}
